import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Reveal from './Reveal';
import { getExhibitedArtists } from '../lib/data';
import { useLanguage } from '../lib/LanguageContext';

const SelectedWorks = () => {
  const { language } = useLanguage();
  const ro = language === 'ro';
  const works = getExhibitedArtists(language)
    .flatMap((artist) =>
      artist.works.map((work) => ({
        ...work,
        artist: artist.name,
      }))
    )
    .filter((work) => {
      const source = `${work.title} ${work.details} ${work.originalFilename}`.toLowerCase();
      return work.originalFilename.includes(' - ') && !source.includes('fara detalii') && !source.includes('tehnica dimensiuni');
    })
    .filter((_, index) => [1, 4, 7, 10, 13, 16, 19, 22, 25].includes(index));

  return (
    <section
      id="artists"
      className="bg-bone text-charcoal py-24 md:py-32 lg:py-40 border-t border-charcoal"
      data-testid="selected-works"
    >
      <div className="px-6 md:px-12 lg:px-16">
        <div className="flex items-end justify-between gap-6 mb-14 md:mb-20">
          <Reveal>
            <div>
              <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/60">
                {ro ? 'Lucrări selectate' : 'Selected Works'}
              </span>
              <h2
                className="mt-5 font-sans font-extrabold text-charcoal leading-[0.95] tracking-normal text-5xl md:text-6xl lg:text-7xl"
                data-testid="works-heading"
              >
                {ro ? 'Lucrări selectate' : 'Selected works'}
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="hidden md:block">
            <Link to="/artists" className="editorial-link" data-testid="works-archive-link">
              <span>{ro ? 'Vezi toți artiștii' : 'Browse all artists'}</span>
              <span className="arrow">→</span>
            </Link>
          </Reveal>
        </div>

        <div
          className="columns-1 sm:columns-2 lg:columns-3 gap-6 md:gap-8 lg:gap-10 [column-fill:_balance]"
          data-testid="works-grid"
        >
          {works.map((w, i) => (
            <WorkCard key={i} work={w} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const WorkCard = ({ work, index }) => {
  const aspect = index % 3 === 1 ? 'aspect-[4/3]' : 'aspect-[4/5]';
  return (
    <motion.figure
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.08 }}
      className="gallery-item group mb-6 md:mb-8 lg:mb-10 break-inside-avoid relative"
      data-testid={`work-card-${index}`}
    >
      <div className={`relative w-full ${aspect} overflow-hidden bg-beige`}>
        <img
          src={work.src}
          alt={work.title}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-colors duration-700" />
        <figcaption className="absolute left-4 bottom-4 right-4 text-bone opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div
            className="font-sans font-extrabold text-2xl md:text-3xl leading-tight"
          >
            {work.title}
          </div>
          <div className="mt-1 text-[10px] tracking-eyebrow uppercase text-bone/80">
            {[work.artist, work.details, work.year].filter(Boolean).join(' · ')}
          </div>
        </figcaption>
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-4">
        <div>
          <div
            className="font-sans font-extrabold text-xl md:text-2xl text-charcoal leading-tight"
          >
            {work.title}
          </div>
          <div className="mt-1 text-[11px] tracking-eyebrow uppercase text-charcoal/60">
            {work.artist}
          </div>
          {(work.details || work.year) && (
            <div className="mt-1 text-[11px] tracking-eyebrow uppercase text-charcoal/45 leading-relaxed">
              {[work.details, work.year].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>
    </motion.figure>
  );
};

export default SelectedWorks;
